// src/components/Paavalikko/Paavalikko.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SocialLinks from "../Social/SocialLinks.jsx";
import {
  Menu, X, LogIn, Sun, Spotlight, ChevronDown, Search,
  UserPlus, HelpCircle, LayoutDashboard, LayoutTemplate, Music2, Globe2, LogOut
} from "lucide-react";
import SearchBar from "../SearchBar/SearchBar.jsx";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import {
  getMyBands,
  getMyBandsUserKey,
  normalizeMyBandsResponse,
} from "../../utils/myBandsCache";
import {
  buildDashboardSituationItems,
  summarizeDashboardSituation,
} from "../../utils/dashboardSituation";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import ConfettiLinkLabel from "../Nav/ConfettiLinkLabel";
import DiscoScrollTop from "../ScrollTop/DiscoScrollTop";
import { DASH_USER_QS } from "../../utils/nav";
import LogoLight from "../../assets/logo-buukkeri-dark.webp";
import LogoDark from "../../assets/logo-buukkeri-dark.webp";

/** Pieni luokkien yhdistin */
const cx = (...c) => c.filter(Boolean).join(" ");

/**
 * Hallinnan huomio-kupla:
 * - pysyy näkyvissä, kunnes käyttäjä sulkee sen
 * - sulkeminen muistetaan tämän selainistunnon ajan
 * - piste jää näkyviin niin kauan kuin huomioitavaa löytyy
 */
const DASH_NOTICE_BUBBLE_DISMISSED_KEY =
  "bbx:dashboardNoticeBubbleDismissed";

/** Päänavigaation peruslinkit (desktop & mobiili) */
const navItems = [
  { to: "/", label: "Etusivu" },
  { to: "/blogi", label: "Blogi" },
  { to: "/tietoameista", label: "Tietoa meistä" },
  { to: "/useinkysyttya", label: "UKK" },
];

/** Mobiilissa näytettävät emojit (palvelu-osiolle) */
const MOBILE_EMOJI = {
  "/": "🏠",
  "/blogi": "📰",
  "/tietoameista": "ℹ️",
  "/useinkysyttya": "❓",
};

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  // ---------------------------
  // Auth-tila (memoized)
  // ---------------------------
  const rawAuth = useAuth?.();
  const auth = useMemo(() => rawAuth || {}, [rawAuth]);

  // Salli erilaiset user-rakenteet: user, currentUser, id/_id/user_id/email
  const { user: authUser, currentUser: authCurrentUser, token: authToken, isAuthenticated } = auth;
  const userLike = authUser ?? authCurrentUser ?? null;
  const myBandsCacheKey =
  getMyBandsUserKey(userLike);
  const hasUserId = !!(userLike && (userLike.id || userLike._id || userLike.user_id || userLike.email));

  // Contextin "raakafakta"
  const resolvedIsAuthed = Boolean(
    typeof isAuthenticated === "boolean" ? isAuthenticated : (authToken || hasUserId)
  );

  // Paavalikon oma peilaus, joka pakottaa rerenderin heti
  const [isAuthed, setIsAuthed] = useState(resolvedIsAuthed);

  useEffect(() => {
    setIsAuthed(resolvedIsAuthed);
  }, [resolvedIsAuthed]);


  // Normalisoitu polku ("/" tai ilman trailing-slashia)
  const norm = (p) => (p === "/" ? "/" : p.replace(/\/+$/, ""));
  const path = norm(location.pathname);

  // --- Onboarding-flow'n tunnistus ---
  const isTermsLike = ["/kayttoehdot", "/ohjeet-ja-ehdot", "/tietosuojaseloste"].includes(path);

  const cameFromOnboarding = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if ((sp.get("from") || "").toLowerCase() === "onboarding") return true;
    if (location.state?.fromOnboarding === true) return true;
    if (typeof document !== "undefined" && /\/onboarding(\b|\/|$)/.test(document.referrer || "")) {
      return true;
    }
    return false;
  }, [location.search, location.state]);

  const isOnboardingFlow = useMemo(() => {
    const roots = ["/rekisteroidy", "/verify-email", "/onboarding", "/setup"];
    if (roots.some((p) => path.startsWith(p))) return true;
    if (isTermsLike && cameFromOnboarding) return true;
    return false;
  }, [path, isTermsLike, cameFromOnboarding]);

  // Näytetään "Kirjaudu ulos" vain kun ei olla onboardingissa
  const canShowLogout = isAuthed && !isOnboardingFlow;

  // Näytetään "Hallintapaneeli" vain jos kirjautunut EIKÄ olla onboarding-virrassa
  const canShowDashboard = isAuthed && !isOnboardingFlow;

  // ------------------------------------------------------------
  // HALLINNAN HUOMIOITAVAT ASIAT
  // ------------------------------------------------------------
  const [dashboardSituationItems, setDashboardSituationItems] = useState([]);
  const [showDashboardNoticeBubble, setShowDashboardNoticeBubble] =
    useState(false);

  const dashboardSituation = useMemo(
    () => summarizeDashboardSituation(dashboardSituationItems),
    [dashboardSituationItems]
  );

  const hasDashboardNotice = dashboardSituationItems.length > 0;

  // Aktiivinen varauspyyntö on kiireellisempi kuin profiilihuomio.
  const dashboardNoticeIsUrgent = dashboardSituation.bookingCount > 0;

  /**
   * Haetaan kirjautuneen käyttäjän profiilien tämänhetkiset tehtävät.
   * Tarkistus tehdään myös reitin vaihtuessa, jotta esimerkiksi
   * dashboardissa hoidettu asia poistuu pisteestä käyttäjän poistuessa sivulta.
   */
  useEffect(() => {
    let alive = true;

if (
  !canShowDashboard ||
  path.startsWith("/dashboard")
) {
  // Dashboard hoitaa omat huomionsa itse.
  // Päävalikko ei tee siellä rinnakkaisia API-hakuja.
  setDashboardSituationItems([]);
  setShowDashboardNoticeBubble(false);

  return () => {
    alive = false;
  };
}

    void (async () => {
      try {
const res = await getMyBands({
  cacheKey: myBandsCacheKey,
});

const { items } =
  normalizeMyBandsResponse(res);

                // Navbarin huomiojärjestelmä koskee vain julkaistuja profiileja.
        // Keskeneräisen profiilin puutteet näytetään edelleen dashboardin
        // omassa ohjeistuksessa, mutta niistä ei muistuteta päävalikossa.
        const publishedItems = items.filter(
          (profile) => profile?.is_published === true
        );

        const situation = await buildDashboardSituationItems(publishedItems);

        if (alive) {
          setDashboardSituationItems(situation);

          // Jos kaikki on kunnossa, mahdollinen avoin kupla pois heti.
          if (!situation.length) {
            setShowDashboardNoticeBubble(false);
          }
        }
      } catch (error) {
        console.debug(
          "[Paavalikko] Huomioitavaa-tarkistus epäonnistui:",
          error
        );

        if (alive) {
          setDashboardSituationItems([]);
          setShowDashboardNoticeBubble(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [
  canShowDashboard,
  path,
  myBandsCacheKey,
]);

/**
 * Sulkee Huomioitavaa-kuplan tämän selainistunnon ajaksi.
 * Pieni ilmoituspiste jää edelleen näkyviin.
 */
const dismissDashboardNoticeBubble = useCallback(() => {
  setShowDashboardNoticeBubble(false);

  try {
    sessionStorage.setItem(
      DASH_NOTICE_BUBBLE_DISMISSED_KEY,
      "1"
    );
  } catch {
    // Ei haittaa, jos sessionStorage ei ole käytettävissä.
  }
}, []);

/**
 * Näytä kupla niin kauan kuin käyttäjä itse sulkee sen.
 *
 * Jos kaikki huomioitavat asiat tulevat kuntoon,
 * poistetaan sulkemismuisti. Tällöin myöhemmin syntyvä
 * uusi huomio voi jälleen avata kuplan.
 */
useEffect(() => {
  if (typeof window === "undefined") return;

  if (!hasDashboardNotice) {
    setShowDashboardNoticeBubble(false);

    try {
      sessionStorage.removeItem(
        DASH_NOTICE_BUBBLE_DISMISSED_KEY
      );
    } catch {
      // Ei haittaa.
    }

    return;
  }

  let dismissed = false;

  try {
    dismissed =
      sessionStorage.getItem(
        DASH_NOTICE_BUBBLE_DISMISSED_KEY
      ) === "1";
  } catch {
    // Jos tallennustila ei toimi, kupla voidaan näyttää.
  }

  if (!dismissed) {
    setShowDashboardNoticeBubble(true);
  }
}, [hasDashboardNotice]);

  // Rekisteröitymis-/kirjautumissivut?
  const isRegisterPage = path === "/rekisteroidy";
  const isLoginPage = path === "/kirjaudu";

  // Näytetään "Ota yhteyttä" –CTA kaikkialla paitsi yhteydenottosivulla
  const showContactCTA = path !== "/otayhteytta";

  // Headerin UI-tilat
  const [open, setOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  // showScrollTop ohjaa vain “Ylös”-nappia, ei headerin piilottelua
  const [showScrollTop, setShowScrollTop] = useState(false);
  const lastY = useRef(0);

  // Profiileille-dropdown (desktop)
  const [artistsMenuOpen, setArtistsMenuOpen] = useState(false);
  const artistsMenuRef = useRef(null);
  const artistsButtonRef = useRef(null);

   /** Scroll-käyttäytyminen: autohide + “Ylös”-nappi.
   *
   * ✅ Korjaus “navbar läpsyttää auki/kiinni” -ilmiöön:
   * - lastY päivitetään JOKA tickillä (ei jää jälkeen pienissä “tärinöissä” / smooth-scrollin lopussa)
   * - hystereesi: eri rajat piilolle/näytölle -> ei sahaamista rajan tuntumassa
   * - rAF-throttle: vähemmän state-päivityksiä per scroll
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Rajat (px)
    const SCROLLED_AFTER = 8;    // milloin header saa “scrolled”-ulkoasun
    const HIDE_AFTER_Y   = 120;  // piilota vasta kun ollaan tarpeeksi alhaalla
    const SHOW_BEFORE_Y  = 80;   // näytä aina kun tullaan lähelle yläreunaa
    const IGNORE_DELTA   = 6;    // alle tämän liike = ignoorataan suuntapäätöksissä
    const SHOW_TOP_AFTER = 300;  // “Ylös”-nappi

    lastY.current = window.scrollY || 0;
    let ticking = false;

    const apply = () => {
      ticking = false;

      const y = window.scrollY || 0;
      const prevY = lastY.current;
      const dy = y - prevY;              // + = alas, - = ylös
      const absDy = Math.abs(dy);

      // Scrolled-tila + “Ylös”-nappi voidaan päivittää joka tickillä
      setScrolled(y > SCROLLED_AFTER);
      setShowScrollTop(y > SHOW_TOP_AFTER);

      // Hystereesi: lähellä yläreunaa näytä aina (ei piilottelua)
      if (y < SHOW_BEFORE_Y) {
        setHideHeader(false);
        lastY.current = y; // ✅ pidä aina ajan tasalla
        return;
      }

      // Suuntapäätökset vain kun liike on “oikeaa”, ei mikrotärinää
      if (absDy >= IGNORE_DELTA) {
        const goingDown = dy > 0;

        if (goingDown && y > HIDE_AFTER_Y) {
          // Skrollataan alas riittävän pitkälle -> piilota
          setHideHeader(true);
        } else if (!goingDown) {
          // Skrollataan ylös -> näytä
          setHideHeader(false);
        }
      }

      // ✅ aina lopuksi: päivitä “edellinen y”
      lastY.current = y;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    apply(); // initial

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /** Body-scrollin lukitus mobiilipaneelin ollessa auki */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("bb-mobile-menu-open");

    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("bb-mobile-menu-open");
    };
  }, [open]);

  /** Esc sulkee paneelit */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setArtistsMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Sulje mobiilihaku reitin vaihtuessa */
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname]);

  /** Klikki ulos → sulje artisti-dropdown */
  useEffect(() => {
    if (!artistsMenuOpen) return;
    const onDocClick = (e) => {
      if (!artistsMenuRef.current) return;
      if (
        !artistsMenuRef.current.contains(e.target) &&
        !artistsButtonRef.current?.contains(e.target)
      ) {
        setArtistsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [artistsMenuOpen]);

  /* ===========================
     LINKKIEN TYYLIT (DESKTOP)
     =========================== */
  const baseLink =
    "relative shrink-0 whitespace-nowrap px-2 py-2 rounded-md font-extrabold uppercase transition-colors duration-300 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

  const activeDesk = isDark
    ? (scrolled ? "text-white" : "text-white")
    : (scrolled ? "text-black" : "text-black");

  const inactiveDesk = isDark
    ? (scrolled ? "text-white/80 hover:text-white" : "text-white/40 hover:text-white/90")
    : (scrolled ? "text-black/60 hover:text-black/90" : "text-black/90 hover:text-pink/100");

  /* ===========================
     HEADER-TAUSTAT
     =========================== */
const headerBgWhenScrolled = isDark
  ? "bg-night-900 text-white border-b border-black/50 shadow-lg shadow-black/70"
  : "bg-white text-black border-b border-black/10 shadow-lg shadow-black/10";

const headerBgTop = cx(
  "transition-shadow duration-300",
  isDark
    ? "backdrop-blur-md bg-night-900/70 text-white border-white/10 shadow-black/70"
    : "backdrop-blur-md bg-white/95 text-black border-black/10 shadow-black/10"
);

const headerGradientTop = isDark
  ? "pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-night-900/90 via-night-900/40 to-transparent"
  : "hidden";

  /* ===========================
     MOBIILI: OVERLAY & PANEELI
     =========================== */
  const overlayClass = "bg-black/90";

  // Paneelin tausta
const panelBg = isDark
  ? "text-white bg-[radial-gradient(1200px_500px_at_110%_-10%,rgba(79,70,229,0.25),transparent_60%),radial-gradient(900px_500px_at_-10%_110%,rgba(236,72,153,0.18),transparent_60%),linear-gradient(180deg,#0a0f1c_0%,#0b1020_45%,#090d19_100%)]"
  : "text-slate-950 bg-white";

  /* ===========================
     MOBIILILINKIT
     =========================== */
  const mobileLinkBase =
    "group relative block rounded-xl px-3 py-1.5 text-xs font-semibold leading-5 tracking-wide transition";
const mobileLinkActive = isDark
  ? "bg-white/10 text-white ring-1 ring-white/15 shadow-[0_0_24px_rgba(255,255,255,0.12)]"
  : "bg-sky-50 text-sky-800 ring-1 ring-sky-100 shadow-sm";

const mobileLinkInactive = isDark
  ? "text-white hover:text-white hover:bg-white/5"
  : "text-slate-700 hover:text-slate-950 hover:bg-slate-100";

  /* ===========================
     TEEMAKYTKIN BOX
     =========================== */
  const themeToggleBox = isDark
    ? "border-white/90 bg-white/20 hover:bg-electric/50 focus:ring-2 focus:ring-white/70"
    : "border-pink/90 bg-pink/50 hover:bg-black/30 focus:ring-2 focus:ring-white/80";

  /* ===========================
     LOGOUT-HANDLER
     =========================== */
  const { logout: logoutFn } = auth;
  const loggingOutRef = useRef(false);

  const handleLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    try {
      // Käynnistä logout “taustalla”
      if (typeof logoutFn === "function") {
        logoutFn({ redirect: false, hard: true });
      }
    } catch (e) {
      console.warn("logoutFn heitti virheen", e);
    }

    // Päivitä headerin oma state heti → nappi ja dashboard-linkki katoaa
    setIsAuthed(false);

    // UI-reaktio heti:
    setOpen(false);
    setArtistsMenuOpen(false);
    navigate("/");

    // vapauta lippu seuraavaa klikkausta varten
    setTimeout(() => {
      loggingOutRef.current = false;
    }, 0);
  }, [logoutFn, navigate]);

  return (
    <>

      <style>
        {`
          body.bb-mobile-menu-open a[aria-label="Kysy WhatsAppissa"],
          body.bb-mobile-menu-open a[title="Kysy WhatsAppissa"],
          body.bb-mobile-menu-open button.fixed[style*="left: 24px"][style*="bottom: 24px"],
          body.bb-mobile-menu-open
            [class~="fixed"][class~="inset-x-0"][class~="bottom-0"][class~="z-[70]"] {
            display: none !important;
          }
        `}
      </style>
      {/* HEADER */}
      <motion.header
        initial={false}
        // Piilotus: vähän reilumpi kuin max-korkeus, jotta varjotkin katoaa
        animate={{ y: hideHeader ? -96 : 0, height: scrolled ? 64 : 80 }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className="navbar-fixed fixed top-0 left-0 right-0 z-50"
      >
        <div
          className={cx(
            // Kun headerin height animoidaan, anna korkeuden “valua” alas asti:
            "relative overflow-visible backdrop-blur-md transition-shadow duration-300 h-full",
            scrolled ? headerBgWhenScrolled : headerBgTop
          )}
        >
          {!scrolled && <div className={headerGradientTop} aria-hidden />}

          <div className="h-full w-full px-4 sm:px-6 lg:px-8">
            {/* Huom: header animoi korkeuden (64/80) → sisäcontainerit h-full */}
            <div className="flex h-full items-center justify-between">

              {/* Logo */}
              <NavLink
                to="/"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap py-2 rounded-xl font-customFont2 text-2xl font-extrabold text-black/90 dark:text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Etusivu"
              >
                <img src={LogoLight} alt="" className="h-6 w-auto shrink-0 dark:hidden" />
                <img src={LogoDark}  alt="" className="h-6 w-auto shrink-0 hidden dark:block" />
                <span className="hidden xxsm:inline s24:inline xsm:inline sm:inline md:inline mdd:inline lg:inline xl:inline 2xl:inline">
                  Buukkeri
                </span>
              </NavLink>

              {/* Desktop nav + dropdown */}
              <nav className="hidden shrink-0 items-center gap-1 whitespace-nowrap xl:flex">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => cx(baseLink, isActive ? activeDesk : inactiveDesk)}
                  >
                    {({ isActive }) => (
                      <ConfettiLinkLabel isActive={isActive}>{item.label}</ConfettiLinkLabel>
                    )}
                  </NavLink>
                ))}

                {/* Profiileille-dropdown (desktop) */}
                <div className="relative">
                  <button
                    ref={artistsButtonRef}
                    type="button"
                    className={cx(
                      baseLink,
                      "inline-flex items-center gap-1",
                      location.pathname.startsWith("/liity-buukkeriin") ||
                       location.pathname.startsWith("/ukk-profiileille") ||
                       location.pathname.startsWith("/buukkeri-embed") ||
                        location.pathname.startsWith("/paranoidi") ||
                       location.pathname.startsWith("/verkkosivut-palveluntarjoajalle")
                        ? activeDesk
                        : inactiveDesk
                    )}
                    aria-haspopup="menu"
                    aria-expanded={artistsMenuOpen}
                    onClick={() => setArtistsMenuOpen((o) => !o)}
                    onMouseEnter={() => setArtistsMenuOpen(true)}
                    onMouseLeave={() => setArtistsMenuOpen(false)}
                  >
                    <span>Profiileille</span>
                    <ChevronDown
                      className={cx("h-4 w-4 transition-transform", artistsMenuOpen && "rotate-180")}
                    />
                  </button>

                  <AnimatePresence>
                    {artistsMenuOpen && (
                      <motion.div
                        ref={artistsMenuRef}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        onMouseEnter={() => setArtistsMenuOpen(true)}
                        onMouseLeave={() => setArtistsMenuOpen(false)}
                        className={cx(
                          "absolute right-0 mt-2 w-64 rounded-xl backdrop-blur-md shadow-2xl p-1 z-[60] border",
                          isDark ? "border-white/10 bg-black/95 text-white"
                                 : "border-white/10 bg-black/90 text-white"
                        )}
                        role="menu"
                        aria-label="Profiileille"
                      >

                        <div className="px-3 pb-1 pt-2 text-[11px] font-black uppercase tracking-[0.18em] text-white/50">
                          Profiileille
                        </div>

                        <NavLink
                          to="/liity-buukkeriin"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Liity Buukkeriin</span>
                          </span>
                        </NavLink>

                        <NavLink
                          to="/ukk-profiileille"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <HelpCircle className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Profiilien UKK</span>
                          </span>
                        </NavLink>

                        <div className="my-1 h-px bg-white/10" />

                        <div className="px-3 pb-1 pt-2 text-[11px] font-black uppercase tracking-[0.18em] text-white/50">
                          Työkalut
                        </div>

                        <NavLink
                          to="/buukkeri-embed"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <LayoutTemplate className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Buukkeri Embed</span>
                          </span>
                        </NavLink>

                        <NavLink
                          to="/paranoidi"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Music2 className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Paranoidi</span>
                          </span>
                        </NavLink>

                        <NavLink
                          to="/verkkosivut-palveluntarjoajalle"
                          onClick={() => setArtistsMenuOpen(false)}
                          className={({ isActive }) =>
                            cx(
                              "block rounded-lg px-3 py-2 text-sm",
                              isActive
                                ? "bg-white/10 text-primary"
                                : "hover:bg-white/5"
                            )
                          }
                          role="menuitem"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Globe2 className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Verkkosivut</span>
                          </span>
                        </NavLink>

                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </nav>

              {/* Oikean reunan toiminnot (DESKTOP) */}
              <div className="flex shrink-0 items-center gap-2">

                {/* Desktop: pikanhaku */}
              <div className="hidden 2xl:block 2xl:w-64">
                <SearchBar />
              </div>

                {/* Hakukuvake alle 2xl-koossa */}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setMobileSearchOpen((v) => !v);
                  }}
                  className={cx(
                    "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition 2xl:hidden",
                    isDark
                      ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                      : "border-black/10 bg-black/5 text-black hover:bg-black/10",
                    mobileSearchOpen && (isDark ? "bg-white/20" : "bg-black/10")
                  )}
                  aria-label={mobileSearchOpen ? "Sulje haku" : "Avaa haku"}
                  aria-expanded={mobileSearchOpen}
                  aria-controls="mobile-header-search"
                  title={mobileSearchOpen ? "Sulje haku" : "Avaa haku"}
                >
                  {mobileSearchOpen ? (
                    <X className="h-5 w-5" aria-hidden />
                  ) : (
                    <Search className="h-5 w-5" aria-hidden />
                  )}
                  <span className="sr-only">
                    {mobileSearchOpen ? "Sulje haku" : "Avaa haku"}
                  </span>
                </button>

                {/* Teemakytkin */}
                <NeonButton
                  onClick={toggleTheme}
                  variant="ghost"
                  size="md"
                  iconOnly
                  IconLeft={theme === "dark" ? Sun : Spotlight}
                  aria-pressed={theme === "dark"}
                  aria-label={`Vaihda ${theme === "dark" ? "vaaleaan" : "tummaan"} teemaan`}
                  title={`Vaihda ${theme === "dark" ? "vaaleaan" : "tummaan"} teemaan`}
                  className={cx("!rounded-full hidden xxxsm:inline-flex xxsm:inline-flex xsm:inline-flex s24:inline-flex sm:inline-flex md:inline-flex mdd:inline-flex lg:inline-flex xl:inline-flex 2xl:inline-flex", themeToggleBox)}
                />


                {/* Desktop: tili suoraan näkyvissä, ei dropdownissa */}
                {!isAuthed && !isLoginPage && (
                  <NavLink
                    to="/kirjaudu"
                    className={cx(
                      "hidden shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-extrabold transition xl:inline-flex",
                      isDark
                        ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
                        : "border-black/10 bg-black/5 text-black hover:bg-black/10"
                    )}
                  >
                    <LogIn className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Kirjaudu</span>
                  </NavLink>
                )}

                {!isAuthed && !isRegisterPage && (
                  <NavLink
                    to="/rekisteroidy"
                    className={cx(
                      "hidden min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-extrabold transition-all duration-200 xl:inline-flex",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      isDark
                        ? "border-cyan-200 bg-cyan-300 text-slate-950 shadow-[0_6px_20px_rgba(34,211,238,0.25)] hover:bg-cyan-200 focus-visible:ring-cyan-300 focus-visible:ring-offset-night-900"
                        : "border-slate-950 bg-slate-950 text-white shadow-md hover:bg-slate-800 focus-visible:ring-slate-950 focus-visible:ring-offset-white"
                    )}
                  >
                    <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Luo tili</span>
                  </NavLink>
                )}

{canShowDashboard && (
  <div className="relative hidden shrink-0 xl:block">

    {/* Huomioitavaa-puhekupla */}
    <AnimatePresence>
      {showDashboardNoticeBubble && (
        <motion.div
          initial={{ opacity: 0, y: -4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          className="
            absolute left-1/2 top-full z-[80]
            mt-3 -translate-x-1/2
            flex items-center gap-2
            whitespace-nowrap
            rounded-lg border border-white/10
            bg-slate-950
            px-3 py-2
            text-xs font-bold normal-case text-white
            shadow-xl
          "
        >
          {/* Nuoli Hallinta-nappiin */}
          <span
            className="
              absolute left-1/2 top-0
              h-2 w-2
              -translate-x-1/2 -translate-y-1
              rotate-45
              border-l border-t border-white/10
              bg-slate-950
            "
            aria-hidden
          />

          <span>Huomioitavaa</span>

          {/* Käyttäjä sulkee kuplan itse */}
          <button
            type="button"
            onClick={dismissDashboardNoticeBubble}
            className="
              inline-flex h-5 w-5
              items-center justify-center
              rounded
              text-white/60
              transition
              hover:bg-white/10
              hover:text-white
            "
            aria-label="Sulje ilmoitus"
            title="Sulje"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </motion.div>
      )}
    </AnimatePresence>

    <NavLink
      to={`/dashboard?${DASH_USER_QS}`}
      onClick={dismissDashboardNoticeBubble}
      title={
        hasDashboardNotice
          ? "Hallinta – huomioitavaa"
          : "Hallinta"
      }
      className={cx(
        "relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap overflow-visible rounded-lg border px-3 py-2 text-sm font-extrabold transition",
        isDark
          ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
          : "border-black/10 bg-black/5 text-black hover:bg-black/10"
      )}
    >
      {hasDashboardNotice && (
        <span
          className={cx(
            "absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2",
            isDark ? "ring-night-900" : "ring-lemon",
            dashboardNoticeIsUrgent
              ? "bg-amber-400 animate-pulse"
              : "bg-fuchsia-300"
          )}
          aria-hidden
        />
      )}

      <LayoutDashboard
        className="h-4 w-4 shrink-0"
        aria-hidden
      />

      <span>Hallinta</span>
    </NavLink>
  </div>
)}

                {canShowLogout && (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={cx(
                      "hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition xl:inline-flex",
                      isDark
                        ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
                        : "border-black/10 bg-black/5 text-black hover:bg-black/10"
                    )}
                    aria-label="Kirjaudu ulos"
                    title="Kirjaudu ulos"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                  </button>
                )}

                {/* Desktop: Ota yhteyttä */}
                {showContactCTA && (
                  <NeonButton
                    onClick={() => navigate("/otayhteytta")}
                    variant="primary"
                    size="sm"
                    className="hidden shrink-0 whitespace-nowrap xl:inline-flex"
                    aria-label="Ota yhteyttä"
                    title="Ota yhteyttä"
                    // IconLeft={Phone}
                  >
                    Ota yhteyttä
                  </NeonButton>
                )}

                {/* Mobile burger + Huomioitavaa-kupla */}
                <div className="relative xl:hidden">
                  <AnimatePresence>
                    {showDashboardNoticeBubble && !open && (
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.96 }}
                        transition={{ duration: 0.2 }}
                        className="
                          absolute right-0 top-full z-[80]
                          mt-3
                          flex items-center gap-2
                          whitespace-nowrap
                          rounded-lg border border-white/10
                          bg-slate-950
                          px-3 py-2
                          text-xs font-bold text-white
                          shadow-xl
                        "
                      >
                        {/* Nuoli osoittaa burger/Hallinta-valikkoon */}
                        <span
                          className="
                            absolute right-3 top-0
                            h-2 w-2
                            -translate-y-1 rotate-45
                            border-l border-t border-white/10
                            bg-slate-950
                          "
                          aria-hidden
                        />

                        <span>Huomioitavaa</span>

                        {/* Kupla suljetaan käyttäjän toimesta */}
                        <button
                          type="button"
                          onClick={dismissDashboardNoticeBubble}
                          className="
                            inline-flex h-5 w-5
                            items-center justify-center
                            rounded
                            text-white/60
                            transition
                            hover:bg-white/10
                            hover:text-white
                          "
                          aria-label="Sulje ilmoitus"
                          title="Sulje"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    type="button"
                    onClick={() => {
                      setMobileSearchOpen(false);
                      setOpen(true);
                    }}
                    className={cx(
                      "relative inline-flex h-10 w-10 items-center justify-center rounded-lg border",
                      isDark
                        ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                        : "border-black/10 bg-black/5 text-black hover:bg-black/10"
                    )}
                    aria-label="Avaa valikko"
                    aria-expanded={open}
                    aria-controls="mobile-menu"
                  >
                    {hasDashboardNotice && (
                      <span
                        className={cx(
                          "absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2",
                          isDark ? "ring-night-900" : "ring-lemon",
                          dashboardNoticeIsUrgent
                            ? "bg-amber-400 animate-pulse"
                            : "bg-fuchsia-500"
                        )}
                        aria-hidden
                      />
                    )}

                    <Menu className="h-6 w-6" />
                    <span className="sr-only">Avaa valikko</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
           {/* Mobile: headerin alle avautuva hakupalkki */}
          <AnimatePresence>
            {mobileSearchOpen && (
              <motion.div
                id="mobile-header-search"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className={cx(
                  "absolute left-0 right-0 top-full z-[55] border-t px-4 py-3 shadow-2xl backdrop-blur-xl 2xl:hidden",
                  isDark
                    ? "border-white/10 bg-night-900/95 text-white"
                    : "border-black/10 bg-white/95 text-black"
                )}
              >
                <div className="mx-auto max-w-7xl">
                  <SearchBar />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* MOBIILIVALIKKO */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cx("fixed inset-0 z-50", overlayClass)}
              onClick={() => {
               setOpen(false);
               dismissDashboardNoticeBubble();
             }}
              aria-hidden="true"
            />

            {/* Paneeli */}
            <motion.aside
              key="panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className={cx("fixed right-0 top-0 z-50 h-full w-[84%] max-w-sm shadow-2xl overflow-hidden flex flex-col", panelBg)}
              role="dialog"
              aria-label="Päävalikko"
              id="mobile-menu"
            >
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="font-customFont2 text-xl">Valikko</span>

              {/* Sulje-nappi */}
                <button
                  onClick={() => setOpen(false)}
                  className={cx(
                    "inline-flex h-9 w-9 items-center justify-center rounded-lg border shadow",
                    isDark
                      ? "border-white/70 bg-white/20 text-white hover:bg-white/10"
                      : "border-slate-300 bg-white text-slate-900 hover:bg-slate-100"
                  )}
                  aria-label="Sulje valikko"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Hehku-tausta */}
              <motion.div
                className="pointer-events-none absolute -top-24 -left-24 h-56 w-56 rounded-full blur-3xl"
                style={{ background: "rgba(236,72,153,0.22)" }}
                animate={{ x: [0, 16, -8, 0], y: [0, -10, 12, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full blur-3xl"
                style={{ background: "rgba(59,130,246,0.18)" }}
                animate={{ x: [0, -20, 10, 0], y: [0, 14, -10, 0] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
              />

               {/* Sisältö scrollaa (jotta kaikki mahtuu myös pieniin näyttöihin) */}
               <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-3 relative">
                {/* ✨ Palvelu */}
                 <div className="mt-2">
                   <h4 className="px-3 py-1 font-customFont2 font-semibold text-sm uppercase tracking-widest text-slate-700 dark:text-white/90">
                   Palvelu
                  </h4>
                  <ul className="space-y-0">
                    {navItems.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                          }
                        >
                          <span className="mr-2">{MOBILE_EMOJI[item.to] || "🔗"}</span>
                          {item.label}
                        </NavLink>
                      </li>
                    ))}

                    {/* Vain mobiiliin: Yhteistyö-sivu */}
                    <li>
                      <NavLink
                        to="/yhteistyo"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(mobileLinkBase, isActive ? mobileLinkActive : mobileLinkInactive)
                        }
                      >
                        🤝 Yhteistyö
                      </NavLink>
                    </li>
                  </ul>
                </div>

                {/* Profiileille */}
                <div className="mt-3">
                  <h4 className="px-3 py-1 font-customFont2 text-sm font-semibold uppercase tracking-widest text-slate-700 dark:text-white/90">
                    Profiileille
                  </h4>

                  <ul className="space-y-0">
                    <li>
                      <NavLink
                        to="/liity-buukkeriin"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            mobileLinkBase,
                            isActive ? mobileLinkActive : mobileLinkInactive
                          )
                        }
                      >
                        🎤 Liity Buukkeriin
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/ukk-profiileille"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            mobileLinkBase,
                            isActive ? mobileLinkActive : mobileLinkInactive
                          )
                        }
                      >
                        📖 Profiilien UKK
                      </NavLink>
                    </li>
                  </ul>
                </div>

                {/* Työkalut */}
                <div className="mt-3">
                  <h4 className="px-3 py-1 font-customFont2 text-sm font-semibold uppercase tracking-widest text-slate-700 dark:text-white/90">
                    Työkalut
                  </h4>

                  <ul className="space-y-0">
                    <li>
                      <NavLink
                        to="/buukkeri-embed"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            mobileLinkBase,
                            isActive ? mobileLinkActive : mobileLinkInactive
                          )
                        }
                      >
                        🧩 Buukkeri Embed
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/paranoidi"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            mobileLinkBase,
                            isActive ? mobileLinkActive : mobileLinkInactive
                          )
                        }
                      >
                        🎵 Paranoidi
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/verkkosivut-palveluntarjoajalle"
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cx(
                            mobileLinkBase,
                            isActive ? mobileLinkActive : mobileLinkInactive
                          )
                        }
                      >
                        🌐 Verkkosivut
                      </NavLink>
                    </li>
                  </ul>
                </div>

                  {/* Some-linkit – näkyy vain ≥ md (ei mobiilissa) */}
                  <div className="mt-4 hidden md:block">
                    <div className="mx-1 mb-2 h-px bg-white/10" />
                    <p className="mb-2 px-4 text-center text-xs uppercase tracking-wide text-white/70">
                      Seuraa meitä
                    </p>
                    <SocialLinks
                      variant="mobile"
                      onItemClick={() => setOpen(false)}
                    />
                  </div>
                </div>

                {/* Tili näkyy suoraan paneelin alareunassa */}
                {(!isAuthed || canShowDashboard || canShowLogout) && (
                  <div className="relative border-t border-white/10 bg-black/15 px-3 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] backdrop-blur-md">
                    {!isAuthed && (
                      <div className="flex gap-2">
                        {!isLoginPage && (
                          <NavLink
                            to="/kirjaudu"
                            onClick={() => setOpen(false)}
                           className="
  inline-flex min-h-11 flex-1 items-center justify-center
  gap-2 rounded-xl border border-slate-300 bg-white
  px-3 py-2 text-sm font-extrabold text-slate-900
  transition hover:bg-slate-50
  dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:bg-white/10
"
                          >
                            <LogIn className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Kirjaudu</span>
                          </NavLink>
                        )}

                        {!isRegisterPage && (
                          <NavLink
                            to="/rekisteroidy"
                            onClick={() => setOpen(false)}
                            className={cx(
                              "inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-extrabold transition-all duration-200",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                              isDark
                                ? "border-cyan-200 bg-cyan-300 text-slate-950 shadow-[0_6px_20px_rgba(34,211,238,0.25)] hover:bg-cyan-200 focus-visible:ring-cyan-300 focus-visible:ring-offset-night-900"
                                : "border-cyan-500 bg-cyan-500 text-white shadow-md hover:bg-cyan-600 focus-visible:ring-cyan-500 focus-visible:ring-offset-white"
                            )}
                          >
                            <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                            <span>Luo tili</span>
                          </NavLink>
                        )}
                      </div>
                    )}

                    {isAuthed && (
                      <div className="flex gap-2">
                        {canShowDashboard && (
                         <NavLink
                            to={`/dashboard?${DASH_USER_QS}`}
                            onClick={() => setOpen(false)}
                          className={cx(
                              "relative inline-flex min-h-11 flex-1 items-center justify-center",
                            "gap-2 rounded-xl px-3 py-2 text-sm",
                            "font-extrabold text-white",
                            "border transition-all duration-200",
                            "hover:-translate-y-[1px]",
                          isDark
                            ? [
                                "border-fuchsia-300/25",
                                "bg-gradient-to-r from-fuchsia-600/90 to-violet-700/90",
                                "shadow-[0_8px_24px_rgba(168,85,247,0.22)]",
                                "hover:from-fuchsia-500 hover:to-violet-600",
                              ].join(" ")
                              : [
                                  "border-fuchsia-600/20",
                                  "bg-gradient-to-r from-fuchsia-500 to-violet-600",
                                  "shadow-[0_8px_22px_rgba(168,85,247,0.24)]",
                                  "hover:from-fuchsia-600 hover:to-violet-700",
                                ].join(" ")
                          )}
                          >
                              {hasDashboardNotice && (
                              <span
                                className={cx(
                                  "absolute right-2 top-2 h-2.5 w-2.5 rounded-full ring-2 ring-violet-700",
                                 dashboardNoticeIsUrgent
  ? "bg-amber-400 animate-pulse"
  : "bg-fuchsia-500"
                                )}
                                aria-hidden
                              />
                            )}

                            <LayoutDashboard
                              className="h-4 w-4 shrink-0"
                              aria-hidden
                            />
                            <span>Hallintapaneeli</span>
                          </NavLink>
                        )}

                        {canShowLogout && (
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="
                              inline-flex min-h-11 items-center justify-center
                              gap-2 rounded-xl border border-white/20 bg-white/5
                              px-4 py-2 text-sm font-extrabold text-white
                              transition hover:bg-white/10
                            "
                            aria-label="Kirjaudu ulos"
                            title="Kirjaudu ulos"
                          >
                            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                            <span className="sr-only sm:not-sr-only">
                              Kirjaudu ulos
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* “Ylös”-nappi kun rullattu alas eikä paneeli auki */}
      {showScrollTop && !open && <DiscoScrollTop bottom={24} right={24} variant="eq" label="Ylös" />}
    </>
  );
}